import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  TextField,
  Fade,
  Paper,
  Chip,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { useCart } from "../provider/CartProvider";
import { useSnack } from "../provider/SnackbarProvider";
import { validateCoupon } from "../coupons/services/couponApi";
import { AppliedCoupon } from "../coupons/models/types/couponTypes";
import { useNavigate } from "react-router-dom";
import ROUTES from "../routes/routesModel";

const CartDrawer: React.FC = () => {
  const navigate = useNavigate();
  const {
    cart,
    removeFromCart,
    updateQuantity,
    cartDrawerOpen,
    closeCartDrawer,
    appliedCoupon,
    applyCoupon,
    removeCoupon,
    getSubtotal,
    getDiscountAmount,
    getTotal,
  } = useCart();
  const [couponCode, setCouponCode] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const showSnackbar = useSnack();

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      showSnackbar("error", "אנא הזן קוד קופון");
      return;
    }

    setCouponLoading(true);
    try {
      const response = await validateCoupon(couponCode.trim());

      if (response.valid && response.coupon) {
        const appliedCouponData: AppliedCoupon = {
          id: response.coupon.id,
          code: response.coupon.code,
          name: response.coupon.name,
          discountPercentage: response.coupon.discountPercentage,
          discountAmount: 0, // Will be calculated by the cart provider
        };

        applyCoupon(appliedCouponData);
        setCouponCode("");
        showSnackbar("success", response.message);
      } else {
        showSnackbar("error", response.message);
      }
    } catch (error: any) {
      showSnackbar(
        "error",
        error.response?.data?.message || "שגיאה באימות הקופון"
      );
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    removeCoupon();
    showSnackbar("success", "קופון הוסר בהצלחה");
  };

  const subtotal = getSubtotal();
  const discountAmount = getDiscountAmount();
  const total = getTotal();

  // Check if free shipping coupon is applied
  const appliedCouponCode = appliedCoupon?.code
    ?.toString()
    .toUpperCase()
    .trim();
  const isFreeShippingCoupon = appliedCouponCode === "FREESHIP";
  const isTestCoupon = appliedCouponCode === "TEST1";

  // Shipping is now selected in checkout, not from products
  // But show free shipping if coupon is applied
  const shippingDisplay =
    isFreeShippingCoupon || isTestCoupon ? "₪0" : "ייקבע בקופה";

  return (
    <Drawer
      anchor="left"
      open={cartDrawerOpen}
      onClose={closeCartDrawer}
      sx={{ zIndex: 1401 }}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: 420 },
          maxWidth: "100vw",
          bgcolor: "#fff",
          borderTopRightRadius: 18,
          borderBottomRightRadius: 18,
          boxShadow: "0 8px 32px 0 rgba(4,122,188,0.13)",
          p: 0,
          direction: "rtl",
          display: "flex",
          flexDirection: "column",
          height: "100vh",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderBottom: "1px solid #eee",
          minHeight: 64,
        }}
      >
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ color: "#222", letterSpacing: 0.5 }}
        >
          סל קניות
        </Typography>
        <IconButton onClick={closeCartDrawer}>
          <CloseIcon />
        </IconButton>
      </Box>
      {/* Cart Items and Summary (scrollable area) */}
      <Box sx={{ flex: "1 1 auto", overflowY: "auto", pb: 2 }}>
        {/* Cart Items */}
        <Box sx={{ px: 2, py: 1, minHeight: 120 }}>
          {cart && Array.isArray(cart) && cart.length === 0 && (
            <Typography
              mt={4}
              textAlign="center"
              color="text.secondary"
              fontSize={20}
              fontWeight={500}
            >
              העגלה שלך ריקה
            </Typography>
          )}
          {cart &&
            Array.isArray(cart) &&
            cart.map((item) => {
              // Create a unique key that includes ID, options, and color to avoid duplicate keys
              const uniqueKey = `${item.id}-${JSON.stringify(
                item.selectedOptions || {}
              )}-${item.selectedColor || ""}`;
              return (
                <Fade in key={uniqueKey}>
                  <Paper
                    elevation={0}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                      p: 1.5,
                      borderRadius: 3,
                      boxShadow: "0 1px 6px 0 rgba(4,122,188,0.07)",
                    }}
                  >
                    <IconButton
                      onClick={() =>
                        removeFromCart(
                          item.id,
                          item.selectedOptions,
                          item.selectedColor
                        )
                      }
                      size="small"
                      sx={{ alignSelf: "flex-start", color: "#d32f2f", ml: 1 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                    <Box
                      component="img"
                      src={item.image}
                      alt={item.name}
                      sx={{
                        width: 60,
                        height: 60,
                        objectFit: "cover",
                        borderRadius: 2,
                        boxShadow: "0 1px 4px 0 rgba(4,122,188,0.10)",
                        border: "1.5px solid #e3f0ff",
                        ml: 1,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        fontWeight={700}
                        fontSize={16}
                        sx={{
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                      >
                        {item.description}
                      </Typography>
                      {/* Display selected options if they exist */}
                      {item.selectedOptions &&
                        Object.keys(item.selectedOptions).length > 0 && (
                          <Box
                            sx={{
                              mt: 1,
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                            }}
                          >
                            {Object.entries(item.selectedOptions).map(
                              ([optionName, optionValue]) => (
                                <Chip
                                  key={optionName}
                                  label={`${optionName}: ${optionValue}`}
                                  size="small"
                                  sx={{
                                    bgcolor: "#e3f0ff",
                                    color: "#047ABC",
                                    fontSize: "0.75rem",
                                    height: 20,
                                  }}
                                />
                              )
                            )}
                          </Box>
                        )}
                      {/* Display selected color if it exists */}
                      {item.selectedColor && (
                        <Box sx={{ mt: 1 }}>
                          <Chip
                            label={`צבע: ${item.selectedColor}`}
                            size="small"
                            sx={{
                              bgcolor: "#e8f5e8",
                              color: "#2e7d32",
                              fontSize: "0.75rem",
                              height: 20,
                            }}
                          />
                        </Box>
                      )}
                      <Typography
                        fontWeight={600}
                        color="#047ABC"
                        fontSize={15}
                      >
                        {item.price.toFixed(2)} ₪
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        minWidth: 90,
                        justifyContent: "flex-end",
                      }}
                    >
                      <IconButton
                        onClick={() =>
                          updateQuantity(
                            item.id,
                            Math.max(1, item.quantity - 1),
                            item.selectedOptions,
                            item.selectedColor
                          )
                        }
                        size="small"
                        sx={{
                          background: "#e3f0ff",
                          color: "#047ABC",
                          borderRadius: 2,
                          fontWeight: 700,
                          fontSize: 18,
                          p: 0.5,
                        }}
                      >
                        -
                      </IconButton>
                      <Typography mx={1} fontWeight={700} fontSize={16}>
                        {item.quantity}
                      </Typography>
                      <IconButton
                        onClick={() =>
                          updateQuantity(
                            item.id,
                            item.quantity + 1,
                            item.selectedOptions,
                            item.selectedColor
                          )
                        }
                        size="small"
                        sx={{
                          background: "#e3f0ff",
                          color: "#047ABC",
                          borderRadius: 2,
                          fontWeight: 700,
                          fontSize: 18,
                          p: 0.5,
                        }}
                      >
                        +
                      </IconButton>
                    </Box>
                    <Typography
                      fontWeight={700}
                      fontSize={16}
                      color="#113355"
                      sx={{ minWidth: 60, textAlign: "left" }}
                    >
                      {(item.price * item.quantity).toFixed(2)} ₪
                    </Typography>
                  </Paper>
                </Fade>
              );
            })}
        </Box>
      </Box>
      {/* Bottom area: summary, checkout button and payment icons */}
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: { xs: 15, md: 2 },
          bgcolor: "#fff",
          borderTop: "1px solid #eee",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Cart Summary (with coupon) */}
        <Box
          sx={{
            py: 2,
            borderTop: "1px solid #eee",
            bgcolor: "#fafbfc",
            borderRadius: 2,
            px: 2,
            mb: 1,
          }}
        >
          {/* Applied Coupon Display */}
          {appliedCoupon && (
            <Box sx={{ mb: 2 }}>
              <Alert
                severity="success"
                sx={{
                  mb: 1,
                  direction: "rtl",
                  display: "flex",
                  flexDirection: "row-reverse",
                  alignItems: "center",
                  justifyContent: "space-between",
                  pr: 2,
                  pl: 1,
                }}
                icon={false}
                action={
                  <IconButton
                    color="inherit"
                    size="small"
                    onClick={handleRemoveCoupon}
                    sx={{ mr: 1, alignSelf: "center" }}
                  >
                    <CloseIcon fontSize="medium" />
                  </IconButton>
                }
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                  }}
                >
                  <Typography fontWeight={700} fontSize={18} color="#205c2a">
                    קופון הופעל בהצלחה!{" "}
                    <span dir="ltr">({appliedCoupon.code})</span>
                  </Typography>
                  <Typography
                    fontSize={15}
                    color="text.secondary"
                    fontWeight={500}
                  >
                    {appliedCoupon.discountPercentage}% הנחה -{" "}
                    {appliedCoupon.name}
                  </Typography>
                </Box>
              </Alert>
            </Box>
          )}

          {/* Coupon Input */}
          {!appliedCoupon && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                bgcolor: "#f3f6fa",
                border: "1px solid #e3f0ff",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: 1,
              }}
            >
              <Typography
                fontWeight={500}
                fontSize={15}
                color="#888"
                sx={{ mb: 1 }}
              >
                יש לך קוד קופון?
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  placeholder="הזן כאן את הקופון..."
                  variant="standard"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleApplyCoupon()}
                  disabled={couponLoading}
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      bgcolor: "#f3f6fa",
                      borderRadius: 2,
                      px: 1,
                      fontSize: 15,
                    },
                  }}
                  sx={{ flex: 1, ml: 1 }}
                />
                <Button
                  variant="contained"
                  onClick={handleApplyCoupon}
                  disabled={couponLoading || !couponCode.trim()}
                  sx={{
                    bgcolor: "#d2f5e3",
                    color: "#1976d2",
                    fontWeight: 700,
                    borderRadius: 2,
                    px: 2,
                    minWidth: 90,
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#b2ebc5" },
                  }}
                >
                  {couponLoading ? "..." : "החל קופון"}
                </Button>
              </Box>
            </Box>
          )}

          {/* Subtotal */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography fontWeight={500} fontSize={16} color="#222">
              סכום ביניים
            </Typography>
            <Typography fontWeight={700} fontSize={15} color="#222">
              ₪{subtotal.toFixed(2)}
            </Typography>
          </Box>

          {/* Discount */}
          {appliedCoupon && discountAmount > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography fontWeight={500} fontSize={16} color="#222">
                הנחה
              </Typography>
              <Typography fontWeight={700} fontSize={15} color="#4caf50">
                -₪{discountAmount.toFixed(2)}
              </Typography>
            </Box>
          )}

          {/* Shipping - will be selected in checkout */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography fontWeight={500} fontSize={16} color="#222">
              משלוח
            </Typography>
            <Typography
              fontWeight={500}
              fontSize={14}
              color={isFreeShippingCoupon || isTestCoupon ? "#4caf50" : "#666"}
            >
              {shippingDisplay}
            </Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          {/* Total */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography fontWeight={700} fontSize={18} color="#113355">
              סה"כ
            </Typography>
            <Typography fontWeight={900} fontSize={22} color="#047ABC">
              ₪{total.toFixed(2)}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => {
            closeCartDrawer();
            navigate(ROUTES.CHECKOUT);
          }}
          disabled={cart && Array.isArray(cart) && cart.length === 0}
          sx={{
            py: 1.5,
            fontWeight: 700,
            fontSize: 18,
            borderRadius: 2,
            background: "linear-gradient(90deg, #047ABC 0%, #1976d2 100%)",
            boxShadow: "0 2px 8px 0 rgba(4,122,188,0.13)",
            transition: "background 0.2s",
            "&:hover": {
              background: "linear-gradient(90deg, #1976d2 0%, #047ABC 100%)",
            },
            "&:disabled": {
              background: "#cccccc",
              color: "#666666",
            },
          }}
        >
          לסיום קנייה (₪{total.toFixed(0)})
        </Button>
        <Box
          display="flex"
          gap={1.5}
          justifyContent="center"
          alignItems="center"
          flexWrap="wrap"
          mt={1}
          mb={{ xs: 2, sm: 0 }}
        >
          {["visa", "masterCard", "paypal", "maestro"].map((icon) => (
            <Box
              key={icon}
              sx={{
                background: "#fff",
                borderRadius: 2,
                p: 0.5,
                boxShadow: "0 1px 4px 0 rgba(4,122,188,0.07)",
              }}
            >
              <Box
                component="img"
                src={`/assets/icons/${icon}.svg`}
                alt={icon}
                sx={{ height: 28 }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Drawer>
  );
};

export default CartDrawer;
